/**
 * BSC Costs (BAB) — `/bab-bsc-costs`
 *
 * Transparency over the Business-Support block of the BAB (lines 12400-13900, subtotal
 * 14000): where each company's shared-service cost comes from, how the allocation rules move
 * it between companies and business units, and whether the model's invariants hold.
 *
 * Read-only. The allocation rules stay maintained in bab-process-app-v2, which shares the
 * tables with the nightly ADF path — a third write site would only deepen the double
 * maintenance. Model reference: `context/docs/bab-bsc-allocation-model.md`.
 *
 * Frozen and live data are never mixed in one number: the reconciliation compares them and
 * states the difference rather than preferring one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertCircle, Info } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { babBscApi, toApiFilters } from '../services/babBscApi';
import type {
    BscPeriod, BscDimensions, BscReconciliation, BscFiltersState,
} from '../types/babBsc';
import { BscReconciliationPanel } from '../components/bab-bsc/BscReconciliationPanel';

type Tab = 'reconciliation' | 'flow' | 'variance' | 'model';

const TABS: { key: Tab; label: string; ready: boolean }[] = [
    { key: 'reconciliation', label: 'Reconciliation', ready: true },
    { key: 'variance', label: 'Variance', ready: false },
    { key: 'flow', label: 'Flow', ready: false },
    { key: 'model', label: 'Model', ready: false },
];

const DEFAULT_FILTERS: BscFiltersState = {
    periodYear: null,
    periodMonth: null,
    selectedBabCompanyCode: 'ALL',
    selectedBuCode: 'ALL',
    selectedSourceCompany: '',
    amountScale: 'units',
};

const SELECT_CLASS =
    'border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

export function BabBscCostsPage() {
    const { canView, isLoading: permsLoading } = usePagePermissions('/bab-bsc-costs');

    const [tab, setTab] = useState<Tab>('reconciliation');
    const [filters, setFilters] = useState<BscFiltersState>(DEFAULT_FILTERS);
    const [periods, setPeriods] = useState<BscPeriod[]>([]);
    const [dimensions, setDimensions] = useState<BscDimensions | null>(null);
    const [reconciliation, setReconciliation] = useState<BscReconciliation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // ---- reference data ----------------------------------------------------
    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        (async () => {
            try {
                const [p, d] = await Promise.all([babBscApi.getPeriods(), babBscApi.getDimensions()]);
                if (cancelled) return;
                setPeriods(p);
                setDimensions(d);
                // Default to the newest FROZEN period: the tie-out needs a freeze to compare
                // against, and it is what the published BAB shows.
                const first = p.find(x => x.isFrozen) ?? p[0];
                if (first) {
                    setFilters(f => ({ ...f, periodYear: first.periodYear, periodMonth: first.periodMonth }));
                }
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load reference data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [canView]);

    // ---- the reconciliation itself ----------------------------------------
    const loadReconciliation = useCallback(async () => {
        if (!filters.periodYear || !filters.periodMonth) return;
        setLoading(true);
        setError(null);
        try {
            const data = await babBscApi.getReconciliation(
                filters.periodYear,
                filters.periodMonth,
                toApiFilters(filters)
            );
            setReconciliation(data);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load reconciliation');
            setReconciliation(null);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        if (tab === 'reconciliation') void loadReconciliation();
    }, [tab, loadReconciliation]);

    const selectedPeriod = useMemo(
        () => periods.find(p => p.periodYear === filters.periodYear && p.periodMonth === filters.periodMonth),
        [periods, filters.periodYear, filters.periodMonth]
    );

    // Source companies narrow to the chosen BAB company; only offered when there is a choice.
    const sourceOptions = useMemo(() => {
        if (!dimensions || filters.selectedBabCompanyCode === 'ALL') return [];
        const list = dimensions.sourceCompanies.filter(
            c => c.babCompanyCode === filters.selectedBabCompanyCode
        );
        return list.length > 1 ? list : [];
    }, [dimensions, filters.selectedBabCompanyCode]);

    if (permsLoading) {
        return <div className="p-6 text-gray-500">Loading…</div>;
    }
    if (!canView) {
        return (
            <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                        <p className="font-medium text-amber-900">No access to BSC Costs</p>
                        <p className="text-sm text-amber-700">
                            Ask an administrator to grant this page under Settings → Permission Matrix.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-4">
            {/* ---- header --------------------------------------------------------- */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">BSC Costs (BAB)</h1>
                    <p className="text-sm text-gray-500">
                        Business-Support block, lines 12400–13900 (subtotal 14000) — sources,
                        allocation and reconciliation. All amounts in EUR.
                    </p>
                </div>
                {tab === 'reconciliation' && (
                    <button
                        onClick={() => void loadReconciliation()}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                )}
            </div>

            {/* ---- tabs ----------------------------------------------------------- */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex gap-6">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`py-2 px-1 border-b-2 text-sm font-medium ${
                                tab === t.key
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {t.label}
                            {!t.ready && <span className="ml-1.5 text-xs text-gray-400">(soon)</span>}
                        </button>
                    ))}
                </nav>
            </div>

            {/* ---- filters -------------------------------------------------------- */}
            <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Period</label>
                    <select
                        className={SELECT_CLASS}
                        value={selectedPeriod ? `${selectedPeriod.periodYear}-${selectedPeriod.periodMonth}` : ''}
                        onChange={e => {
                            const [y, m] = e.target.value.split('-').map(Number);
                            setFilters(f => ({ ...f, periodYear: y, periodMonth: m }));
                        }}
                    >
                        {periods.map(p => (
                            <option key={p.label} value={`${p.periodYear}-${p.periodMonth}`}>
                                {p.label}{p.isFrozen ? '' : ' (not frozen)'}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Report Company</label>
                    <select
                        className={SELECT_CLASS}
                        value={filters.selectedBabCompanyCode}
                        onChange={e => setFilters(f => ({
                            ...f,
                            selectedBabCompanyCode: e.target.value,
                            selectedSourceCompany: '',
                        }))}
                    >
                        <option value="ALL">All companies</option>
                        {dimensions?.babCompanies.map(c => (
                            <option key={c.code} value={c.code}>{c.name} ({c.code.toUpperCase()})</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Business Unit</label>
                    <select
                        className={SELECT_CLASS}
                        value={filters.selectedBuCode}
                        onChange={e => setFilters(f => ({ ...f, selectedBuCode: e.target.value }))}
                    >
                        <option value="ALL">All units</option>
                        {dimensions?.businessUnits.map(b => (
                            <option key={b.code} value={b.code}>{b.name} ({b.code})</option>
                        ))}
                    </select>
                </div>

                {sourceOptions.length > 0 && (
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Source Company</label>
                        <select
                            className={SELECT_CLASS}
                            value={filters.selectedSourceCompany}
                            onChange={e => setFilters(f => ({ ...f, selectedSourceCompany: e.target.value }))}
                        >
                            <option value="">All sources</option>
                            {sourceOptions.map(c => (
                                <option key={c.companyCode} value={c.companyCode}>
                                    {c.companyName} ({c.companyCode})
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {selectedPeriod && !selectedPeriod.isFrozen && (
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                        <Info className="w-3.5 h-3.5 flex-shrink-0" />
                        Not frozen — live data only, nothing to compare the freeze against.
                    </div>
                )}
            </div>

            {/* ---- body ----------------------------------------------------------- */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                </div>
            )}

            {tab === 'reconciliation' && (
                loading
                    ? <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading reconciliation…</div>
                    : reconciliation
                        ? <BscReconciliationPanel data={reconciliation} />
                        : !error && <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">No data for this period.</div>
            )}

            {tab !== 'reconciliation' && (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                    <p className="font-medium text-gray-700">
                        {TABS.find(t => t.key === tab)?.label} is not built yet.
                    </p>
                    <p className="mt-1 text-sm">
                        The model behind it is documented in{' '}
                        <span className="font-mono text-xs">context/docs/bab-bsc-allocation-model.md</span>.
                    </p>
                </div>
            )}
        </div>
    );
}
